import { SEO } from '../components/SEO'
import { motion } from 'framer-motion'
import { useEffect, useState } from 'react'
import {
  ArrowRight,
  Brain,
  Check,
  Code,
  Database,
  GitBranch,
  Globe,
  Layers,
  Package,
  Server,
  Shield,
  Smartphone,
  Sparkles,
  Terminal,
  Workflow,
  Zap,
} from 'lucide-react'
import { Link } from 'react-router-dom'
import { useLanguage } from '../contexts/LanguageContext'
import { t } from '../locales/translations'
import { APP_ENTRY_URL } from '../config/brand'

const Home = () => {
  const { language } = useLanguage()

  const [cards, setCards] = useState([
    { id: 1, x: 12, y: 8, vx: 0.8, vy: 0.6 },
    { id: 2, x: -6, y: 38, vx: 0.6, vy: 0.8 },
    { id: 3, x: 74, y: 68, vx: -0.8, vy: 0.6 },
    { id: 4, x: 22, y: 84, vx: 0.6, vy: -0.7 }
  ])

  useEffect(() => {
    const MAX_SPEED = 1.0
    const MIN_SPEED = 0.4

    const animationFrame = setInterval(() => {
      setCards((prevCards) => {
        const nextCards = prevCards.map((card) => ({
          ...card,
          x: card.x + card.vx,
          y: card.y + card.vy
        }))

        nextCards.forEach((card) => {
          if (card.x < -15) {
            card.x = -15
            card.vx = Math.abs(card.vx) * 0.9
          }
          if (card.x > 85) {
            card.x = 85
            card.vx = -Math.abs(card.vx) * 0.9
          }
          if (card.y < -5) {
            card.y = -5
            card.vy = Math.abs(card.vy) * 0.9
          }
          if (card.y > 90) {
            card.y = 90
            card.vy = -Math.abs(card.vy) * 0.9
          }

          const centerMinX = 25
          const centerMaxX = 75
          const centerMinY = 25
          const centerMaxY = 75

          if (card.x > centerMinX && card.x < centerMaxX && card.y > centerMinY && card.y < centerMaxY) {
            const distToLeft = card.x - centerMinX
            const distToRight = centerMaxX - card.x
            const distToTop = card.y - centerMinY
            const distToBottom = centerMaxY - card.y
            const minDist = Math.min(distToLeft, distToRight, distToTop, distToBottom)

            if (minDist === distToLeft) {
              card.x = centerMinX - 1
              card.vx = -Math.abs(card.vx)
            } else if (minDist === distToRight) {
              card.x = centerMaxX + 1
              card.vx = Math.abs(card.vx)
            } else if (minDist === distToTop) {
              card.y = centerMinY - 1
              card.vy = -Math.abs(card.vy)
            } else {
              card.y = centerMaxY + 1
              card.vy = Math.abs(card.vy)
            }
          }

          const speed = Math.sqrt(card.vx * card.vx + card.vy * card.vy)
          if (speed > MAX_SPEED) {
            const scale = MAX_SPEED / speed
            card.vx *= scale
            card.vy *= scale
          } else if (speed < MIN_SPEED && speed > 0) {
            const scale = MIN_SPEED / speed
            card.vx *= scale
            card.vy *= scale
          }
        })

        for (let i = 0; i < nextCards.length; i += 1) {
          for (let j = i + 1; j < nextCards.length; j += 1) {
            const card1 = nextCards[i]
            const card2 = nextCards[j]
            const dx = card2.x - card1.x
            const dy = card2.y - card1.y
            const distance = Math.sqrt(dx * dx + dy * dy)

            if (distance < 22 && distance > 0) {
              const angle = Math.atan2(dy, dx)
              const sin = Math.sin(angle)
              const cos = Math.cos(angle)
              const vx1 = card1.vx * cos + card1.vy * sin
              const vy1 = card1.vy * cos - card1.vx * sin
              const vx2 = card2.vx * cos + card2.vy * sin
              const vy2 = card2.vy * cos - card2.vx * sin
              const newVx1 = vx2
              const newVx2 = vx1

              card1.vx = newVx1 * cos - vy1 * sin
              card1.vy = vy1 * cos + newVx1 * sin
              card2.vx = newVx2 * cos - vy2 * sin
              card2.vy = vy2 * cos + newVx2 * sin

              const overlap = 22 - distance
              const separationX = (overlap / 2 + 1) * cos
              const separationY = (overlap / 2 + 1) * sin
              card1.x -= separationX
              card1.y -= separationY
              card2.x += separationX
              card2.y += separationY
            }
          }
        }

        return nextCards
      })
    }, 40)

    return () => clearInterval(animationFrame)
  }, [])

  const capabilities = [
    { icon: Workflow, key: 'conversation', color: 'primary' },
    { icon: GitBranch, key: 'task', color: 'primary' },
    { icon: Database, key: 'knowledge', color: 'primary' },
    { icon: Terminal, key: 'node', color: 'primary' }
  ]

  const features = [
    { icon: Brain, key: 'cloud' },
    { icon: Shield, key: 'verified' },
    { icon: Layers, key: 'project' },
    { icon: Zap, key: 'crossEnd' }
  ]

  const useCases = language === 'zh'
    ? [
        { icon: Code, title: '官网与数字项目', desc: '把会话、工作区、预览和版本放进同一条持续推进链路。' },
        { icon: Sparkles, title: '内容生产', desc: '从图像、视频到音频任务，让创意结果持续沉淀为资产。' },
        { icon: Server, title: '远程运维', desc: '连接服务器、数据库和缓存，把诊断与执行放在同一工作台。' },
        { icon: Smartphone, title: '跨端执行', desc: '联动桌面、Linux 与 Android 节点，把能力带到真实环境。' }
      ]
    : [
        { icon: Code, title: 'Web & Digital Projects', desc: 'Keep conversation, workspace, preview, and versioning inside one continuous workflow.' },
        { icon: Sparkles, title: 'Creative Production', desc: 'Turn image, video, and audio tasks into reusable creative assets.' },
        { icon: Server, title: 'Remote Ops', desc: 'Bring servers, databases, and caches into one controlled execution surface.' },
        { icon: Smartphone, title: 'Cross-End Execution', desc: 'Coordinate desktop, Linux, and Android nodes in real environments.' }
      ]

  const techStack = [
    {
      name: 'Cloud Orchestration',
      icon: Brain,
      desc: language === 'zh' ? '云端理解与调度中心' : 'Cloud-side understanding and orchestration',
      detail: language === 'zh' ? '负责意图理解、任务路由与状态收敛' : 'Interprets goals, routes work, and converges outcomes'
    },
    {
      name: 'Node Network',
      icon: Terminal,
      desc: language === 'zh' ? '节点执行面' : 'Execution surface across nodes',
      detail: language === 'zh' ? '连接 Windows、Linux、Android 等真实环境' : 'Connects Windows, Linux, Android, and real environments'
    },
    {
      name: 'Project Runtime',
      icon: Package,
      desc: language === 'zh' ? '项目化持续工作' : 'Project-based continuous work',
      detail: language === 'zh' ? '让文件、版本、预览和会话同步沉淀' : 'Keeps files, versions, previews, and chats in sync'
    },
    {
      name: 'Knowledge Layer',
      icon: Database,
      desc: language === 'zh' ? '长期知识与记忆' : 'Long-term knowledge and memory',
      detail: language === 'zh' ? '将过程和结果转化为可复用资产' : 'Turns process and outcomes into reusable assets'
    }
  ]

  const orbitCards = language === 'zh'
    ? [
        { title: '会话驱动任务', subtitle: 'Conversation to Task', desc: '理解目标，不停在回答层。', accent: 'from-primary-dark to-primary', icon: Workflow },
        { title: '云端统一调度', subtitle: 'Cloud Orchestration', desc: '模型、技能与上下文在同一中心汇流。', accent: 'from-primary to-[#ff8da8]', icon: Brain },
        { title: '节点进入环境', subtitle: 'Node Execution', desc: '连接桌面、服务器与移动端执行面。', accent: 'from-secondary-dark to-secondary', icon: Terminal },
        { title: '结果持续沉淀', subtitle: 'Knowledge Assets', desc: '每次工作都成为下一次加速的基础。', accent: 'from-[#f08f2c] to-secondary', icon: Database }
      ]
    : [
        { title: 'Conversation Becomes Work', subtitle: 'Conversation to Task', desc: 'Intent is understood as work, not just text.', accent: 'from-primary-dark to-primary', icon: Workflow },
        { title: 'Cloud-Side Orchestration', subtitle: 'Cloud Orchestration', desc: 'Models, skills, and context converge in one control plane.', accent: 'from-primary to-[#ff8da8]', icon: Brain },
        { title: 'Nodes Enter the Environment', subtitle: 'Node Execution', desc: 'Desktop, server, and mobile surfaces become executable.', accent: 'from-secondary-dark to-secondary', icon: Terminal },
        { title: 'Results Become Assets', subtitle: 'Knowledge Assets', desc: 'Every run compounds into reusable knowledge.', accent: 'from-[#f08f2c] to-secondary', icon: Database }
      ]

  const nodeNetwork = language === 'zh'
    ? {
        eyebrow: 'NODE NETWORK',
        title: '跨端节点网络，正在成为 Avelia 的执行底座',
        subtitle: 'Windows、Linux、Android 会在同一条工作主链中汇合，让 AI 既能理解目标，也能进入真实环境推进工作。',
        bullets: ['受控接入与能力上报', '任务过程可验证、可中断、可恢复', '结果自动回流到项目和知识层']
      }
    : {
        eyebrow: 'NODE NETWORK',
        title: 'A cross-end node network is becoming the execution fabric of Avelia',
        subtitle: 'Windows, Linux, and Android converge on the same work chain so AI can understand a goal and move it inside real environments.',
        bullets: ['Controlled onboarding and capability reporting', 'Verifiable, interruptible, resumable task progress', 'Results flow back into projects and knowledge']
      }

  return (
    <>
      <SEO />
      <div className="pt-0">
        <section className="relative flex min-h-screen items-center justify-center overflow-hidden pt-20">
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute left-[14%] top-[14%] h-56 w-56 rounded-full bg-primary/[0.05] blur-[110px] animate-float-slow"></div>
            <div className="absolute bottom-[18%] right-[12%] h-52 w-52 rounded-full bg-secondary/[0.06] blur-[110px] animate-float" style={{ animationDelay: '2s' }}></div>
            <div
              className="absolute inset-0 opacity-[0.018]"
              style={{
                backgroundImage: 'linear-gradient(rgba(27, 35, 51, 0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(27, 35, 51, 0.06) 1px, transparent 1px)',
                backgroundSize: '64px 64px'
              }}
            ></div>
          </div>

          <div className="relative mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
            <motion.div initial={{ opacity: 0, x: -50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }}>
              <div className="mb-6 inline-flex items-center space-x-2 rounded-full border border-border bg-white/90 px-4 py-2">
                <div className="h-2 w-2 rounded-full bg-success animate-pulse"></div>
                <span className="text-sm font-mono text-text-secondary">{t(language, 'home.hero.status')}</span>
              </div>

              <h1 className="home-hero-title mb-6 text-4xl font-bold leading-tight sm:text-5xl md:text-6xl lg:text-7xl">
                <span className="block text-text-primary" style={{ marginBottom: '20px' }}>{t(language, 'home.hero.title')}</span>
                <span className="home-hero-subtitle mt-2 block text-3xl text-primary-dark sm:text-4xl md:text-5xl">{t(language, 'home.hero.subtitle')}</span>
              </h1>

              <p className="mb-6 text-lg leading-relaxed text-text-secondary sm:text-xl">
                {t(language, 'home.hero.description')}
              </p>

              <div className="mb-8 flex flex-wrap gap-3">
                {['feature1', 'feature2', 'feature3'].map((key) => (
                  <div key={key} className="flex items-center space-x-2 rounded-lg border border-border bg-white px-3 py-1.5 shadow-sm">
                    <Check size={16} className="text-success" />
                    <span className="text-sm text-text-secondary">{t(language, `home.hero.${key}`)}</span>
                  </div>
                ))}
              </div>

              <div className="flex flex-col gap-3 sm:flex-row sm:gap-4">
                <a
                  href={APP_ENTRY_URL}
                  className="group relative rounded-lg bg-primary px-6 py-3 text-base font-semibold text-white shadow-[0_16px_32px_rgba(255,95,135,0.22)] transition-all hover:bg-primary-light hover:shadow-[0_20px_38px_rgba(255,95,135,0.26)] sm:px-8 sm:py-4 sm:text-lg"
                >
                  <span className="relative z-10 flex items-center justify-center gap-2">
                    <Terminal size={18} className="h-[18px] w-[18px] shrink-0" />
                    <span>{t(language, 'home.hero.downloadBtn')}</span>
                    <ArrowRight className="h-[18px] w-[18px] shrink-0 transition-transform group-hover:translate-x-1" />
                  </span>
                </a>

                <Link to="/features" className="rounded-lg border border-border bg-white px-6 py-3 text-base font-semibold text-text-primary shadow-[0_10px_24px_rgba(27,35,51,0.06)] transition-all hover:border-primary/25 hover:bg-primary/5 sm:px-8 sm:py-4 sm:text-lg">
                  {t(language, 'home.hero.featuresBtn')}
                </Link>
              </div>

              <p className="mt-4 text-xs text-text-muted">
                {language === 'zh' ? '云端理解 · 节点执行 · 项目沉淀 · 跨端协作' : 'Cloud understanding · Node execution · Project memory · Cross-end collaboration'}
              </p>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.2 }} className="relative w-full">
              <div className="relative h-[400px] w-full sm:h-[500px] lg:h-[600px]">
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.8, duration: 0.6 }}
                  className="absolute left-1/2 top-1/2 z-30 w-80 -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-border bg-white p-4 shadow-[0_24px_60px_rgba(27,35,51,0.14)] sm:w-96 sm:p-6"
                >
                  <div className="mb-4 flex items-center space-x-2">
                    <div className="h-3 w-3 rounded-full bg-red-500"></div>
                    <div className="h-3 w-3 rounded-full bg-yellow-500"></div>
                    <div className="h-3 w-3 rounded-full bg-green-500"></div>
                    <span className="ml-2 font-mono text-xs text-text-muted">avelia.runtime</span>
                  </div>
                  <div className="space-y-2 font-mono text-xs sm:text-sm">
                    <div className="text-success">$ avelia continue objective</div>
                    <div className="text-text-secondary">{language === 'zh' ? '→ 构建上下文与任务边界...' : '→ Building context and task boundaries...'}</div>
                    <div className="text-primary">{language === 'zh' ? '✓ 云端调度已就绪' : '✓ Cloud orchestration online'}</div>
                    <div className="text-secondary">{language === 'zh' ? '✓ 节点能力已接入' : '✓ Node capabilities connected'}</div>
                    <div className="text-success">{language === 'zh' ? '✓ 结果回流至项目与知识层' : '✓ Outcomes merged into project and knowledge layers'}</div>
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: [0, 1, 0] }}
                      transition={{ delay: 2.2, duration: 1, repeat: Infinity }}
                      className="inline-block text-text-muted"
                    >
                      █
                    </motion.div>
                  </div>
                </motion.div>

                {orbitCards.map((card, index) => (
                  <motion.div
                    key={card.title}
                    style={{ left: `${cards[index].x}%`, top: `${cards[index].y}%` }}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ opacity: { delay: 1.2 + index * 0.2, duration: 0.6 }, scale: { delay: 1.2 + index * 0.2, duration: 0.6 } }}
                    whileHover={{ scale: 1.08, zIndex: 50, transition: { duration: 0.3 } }}
                    className="absolute z-10 w-52 cursor-pointer rounded-xl border border-border bg-white p-4 shadow-[0_16px_36px_rgba(27,35,51,0.09)] transition-all hover:-translate-y-1 hover:border-primary/30 hover:shadow-[0_22px_44px_rgba(27,35,51,0.12)] sm:w-60"
                  >
                    <div className="mb-2 flex items-center space-x-3">
                      <div className={`flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br ${card.accent} text-white shadow-[0_12px_24px_rgba(255,95,135,0.18)]`}>
                        <card.icon size={20} />
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-text-primary">{card.title}</div>
                        <div className="text-xs text-text-secondary">{card.subtitle}</div>
                      </div>
                    </div>
                    <div className="text-xs leading-relaxed text-text-secondary">{card.desc}</div>
                  </motion.div>
                ))}

                <div className="pointer-events-none absolute inset-0 rounded-3xl bg-gradient-to-br from-primary/[0.02] via-transparent to-secondary/[0.03] blur-[70px]"></div>
              </div>
            </motion.div>
          </div>
        </section>

        <section className="relative py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="mb-16 text-center">
              <h2 className="mb-4 text-4xl font-bold md:text-5xl">
                <span className="text-text-primary">{t(language, 'home.capabilities.title')}</span>
              </h2>
              <p className="mx-auto max-w-3xl text-xl text-text-secondary">{t(language, 'home.capabilities.subtitle')}</p>
            </motion.div>

            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
              {capabilities.map((capability, index) => (
                <motion.div key={capability.key} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }} className="group cursor-pointer rounded-2xl border border-border p-6 transition-all glass-effect hover:-translate-y-1 hover:border-primary/30">
                  <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-xl border border-primary/30 bg-primary/20 transition-colors group-hover:bg-primary/30">
                    <capability.icon size={28} className="text-primary" />
                  </div>
                  <h3 className="mb-2 text-xl font-bold text-text-primary">{t(language, `home.capabilities.${capability.key}.title`)}</h3>
                  <p className="mb-3 text-sm text-text-secondary">{t(language, `home.capabilities.${capability.key}.subtitle`)}</p>
                  <p className="text-sm leading-relaxed text-text-secondary">{t(language, `home.capabilities.${capability.key}.desc`)}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="relative border-y border-border/70 bg-void-lighter/70 py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="mb-16 text-center">
              <h2 className="mb-4 text-4xl font-bold text-text-primary md:text-5xl">
                {language === 'zh' ? '典型落地场景' : 'Typical Launch Scenarios'}
              </h2>
              <p className="mx-auto max-w-3xl text-xl text-text-secondary">
                {language === 'zh'
                  ? 'Avelia 不把能力堆成孤立工具，而是把创意、工程、运维和执行连接成持续推进的工作系统。'
                  : 'Avelia does not stack isolated tools. It connects creative work, engineering, operations, and execution into one continuous system.'}
              </p>
            </motion.div>

            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
              {useCases.map((useCase, index) => (
                <motion.div key={useCase.title} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }} className="rounded-xl border border-border p-6 transition-all glass-effect hover:-translate-y-1 hover:border-primary/30">
                  <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg border border-primary/20 bg-primary/10">
                    <useCase.icon size={24} className="text-primary" />
                  </div>
                  <h3 className="mb-2 text-lg font-bold text-text-primary">{useCase.title}</h3>
                  <p className="text-sm leading-relaxed text-text-secondary">{useCase.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="relative py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="mb-16 text-center">
              <h2 className="mb-4 text-4xl font-bold md:text-5xl">
                <span className="text-text-primary">{t(language, 'home.features.title')}</span>
              </h2>
              <p className="mx-auto max-w-3xl text-xl text-text-secondary">{t(language, 'home.features.subtitle')}</p>
            </motion.div>

            <div className="mb-12 grid gap-8 md:grid-cols-2">
              {features.map((feature, index) => (
                <motion.div key={feature.key} initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="rounded-2xl border border-border p-8 transition-all glass-effect hover:border-primary/30">
                  <div className="flex items-start space-x-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg border border-primary/30 bg-primary/20">
                      <feature.icon className="text-primary" size={24} />
                    </div>
                    <div>
                      <h3 className="mb-2 text-2xl font-bold text-text-primary">{t(language, `home.features.${feature.key}.title`)}</h3>
                      <p className="leading-relaxed text-text-secondary">{t(language, `home.features.${feature.key}.desc`)}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="rounded-2xl border border-border p-8 glass-effect">
              <div className="mb-8 text-center">
                <h3 className="mb-2 text-2xl font-bold text-text-primary">
                  {language === 'zh' ? '平台结构' : 'Platform Structure'}
                </h3>
                <p className="text-text-secondary">
                  {language === 'zh' ? '从云端理解到节点执行，Avelia 用统一架构承接长期工作。' : 'From cloud-side understanding to node execution, Avelia uses one architecture for long-running work.'}
                </p>
              </div>
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
                {techStack.map((tech, index) => (
                  <motion.div key={tech.name} initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: index * 0.05 }} className="group">
                    <div className="h-full rounded-xl border border-border p-4 transition-all glass-effect hover:border-primary/30">
                      <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-lg border border-primary/20 bg-primary/10 transition-colors group-hover:bg-primary/20">
                        <tech.icon size={24} className="text-primary" />
                      </div>
                      <h4 className="mb-1 text-center text-sm font-bold text-text-primary">{tech.name}</h4>
                      <p className="mb-2 text-center text-xs text-text-secondary">{tech.desc}</p>
                      <p className="text-center text-xs text-text-muted">{tech.detail}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        <section className="relative border-y border-border/70 bg-void-lighter/70 py-20">
          <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="rounded-3xl border border-border p-8 glass-effect sm:p-10">
              <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-center">
                <div className="flex-1">
                  <div className="mb-4 inline-flex items-center space-x-2 rounded-full border border-secondary/20 bg-secondary/10 px-3 py-1">
                    <span className="text-xs font-mono text-secondary">{nodeNetwork.eyebrow}</span>
                  </div>
                  <h2 className="mb-3 text-2xl font-bold text-text-primary sm:text-3xl">{nodeNetwork.title}</h2>
                  <p className="mb-4 leading-relaxed text-text-secondary">{nodeNetwork.subtitle}</p>
                  <div className="space-y-2 text-sm text-text-secondary">
                    {nodeNetwork.bullets.map((item) => (
                      <div key={item} className="flex items-start space-x-2">
                        <Check size={16} className="mt-0.5 text-success" />
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="relative h-32 w-32 shrink-0 rounded-full border-2 border-primary/15 bg-white sm:h-40 sm:w-40">
                  <div className="absolute inset-5 rounded-full bg-gradient-to-br from-primary/12 to-secondary/18"></div>
                  <div className="absolute inset-0 rounded-full border-2 border-primary/10 animate-ping" style={{ animationDuration: '3s' }}></div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Globe size={42} className="text-primary-dark" />
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        <section className="relative py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="relative overflow-hidden rounded-3xl border border-border p-12 text-center glass-effect">
              <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-transparent to-secondary/5"></div>
              <div className="relative">
                <h2 className="mb-6 text-4xl font-bold text-text-primary md:text-5xl">{t(language, 'home.cta.title')}</h2>
                <p className="mx-auto mb-8 max-w-2xl text-xl text-text-secondary">{t(language, 'home.cta.subtitle')}</p>

                <a
                  href={APP_ENTRY_URL}
                  className="inline-flex items-center space-x-2 rounded-full bg-gradient-to-r from-primary to-secondary px-8 py-4 text-lg font-semibold text-white transition-all hover:shadow-2xl hover:shadow-primary/20"
                >
                  <Terminal size={20} />
                  <span>{t(language, 'home.cta.downloadBtn')}</span>
                  <ArrowRight size={18} />
                </a>

                <p className="mt-6 text-sm text-text-muted">{t(language, 'home.cta.hint')}</p>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  )
}

export default Home
